import { Fragment, useMemo, useState } from "react";
import { Laptop, Search } from "lucide-react";
import type { MachineSummary, UpdateChannel } from "@racler/protocol";
import { ConfirmDialog } from "../../components/Dialog.tsx";
import { api, errorText } from "../../lib/api.ts";
import { requestErrorText, updateTexts } from "../../lib/desktopUpdates.ts";
import { revokeConfirmation } from "../../lib/revokeMachine.ts";
import { navigate } from "../../lib/router.ts";
import { useMachineUpdates } from "../../lib/useMachineUpdates.ts";
import { useMachines } from "../../lib/useMachines.ts";
import { MachineUpdateCell, MachineVersionCell } from "./MachineUpdateCell.tsx";
import { UpdateConfirmDialog } from "./UpdateConfirmDialog.tsx";

/** Lists the user's Machines and lets the owner revoke one (M3-WP7). Browser sessions are a later milestone. */
export function MachinesSection() {
  const { machines, reload } = useMachines();
  const [revoking, setRevoking] = useState<MachineSummary | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const updates = useMachineUpdates();
  const [updating, setUpdating] = useState<MachineSummary | null>(null);
  const [savingChannel, setSavingChannel] = useState<ReadonlySet<string>>(new Set());
  const [channelErrors, setChannelErrors] = useState<ReadonlyMap<string, string>>(new Map());

  /** Stores the channel only (never installs); one save per Machine at a time. */
  const changeChannel = (machineId: string, channel: UpdateChannel) => {
    if (savingChannel.has(machineId)) return;
    setSavingChannel((prev) => new Set(prev).add(machineId));
    setChannelErrors((prev) => {
      const next = new Map(prev);
      next.delete(machineId);
      return next;
    });
    updates
      .setChannel(machineId, channel)
      .catch((err: unknown) =>
        setChannelErrors((prev) =>
          new Map(prev).set(machineId, updateTexts.channelSaveFailed(requestErrorText(err))),
        ),
      )
      .finally(() =>
        setSavingChannel((prev) => {
          const next = new Set(prev);
          next.delete(machineId);
          return next;
        }),
      );
  };

  const filteredMachines = useMemo(() => {
    if (!machines) return [];
    if (!searchQuery.trim()) return machines;
    const q = searchQuery.toLowerCase();
    return machines.filter((m) => m.name.toLowerCase().includes(q) || m.platform.toLowerCase().includes(q));
  }, [machines, searchQuery]);

  return (
    <>
      <section className="settings-card set-block">
        <div className="settings-card-header">
          <div>
            <h3 className="settings-card-title">Paired Machines</h3>
            <p className="settings-card-desc">
              Development machines with the racler background runtime authorized to connect to your account.
            </p>
          </div>
        </div>

        {done && (
          <p className="small" style={{ color: "#16a34a", margin: 0 }} data-testid="revoke-done">
            {done}
          </p>
        )}

        {machines && machines.length > 2 && (
          <div className="settings-search-bar">
            <Search size={14} className="muted" aria-hidden />
            <input
              type="text"
              className="settings-search-input"
              placeholder="Filter machines..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}

        {machines === null && <p className="muted small">Loading machines…</p>}

        {machines && machines.length === 0 && (
          <div className="home-empty-state" style={{ padding: "28px 16px" }}>
            <Laptop size={32} className="muted" style={{ opacity: 0.5 }} />
            <p className="home-empty-title">No machines paired yet</p>
            <p className="home-empty-desc muted small">
              Pair your Mac or Linux desktop with the racler CLI or desktop app to start working.
            </p>
            <button className="primary" onClick={() => navigate("/pair")}>
              Pair a machine
            </button>
          </div>
        )}

        {machines && machines.length > 0 && filteredMachines.length === 0 && (
          <p className="muted small">No machines match &ldquo;{searchQuery}&rdquo;.</p>
        )}

        {filteredMachines.length > 0 && (
          <table className="kv">
            <thead>
              <tr>
                <th>Machine</th>
                <th>Platform</th>
                <th>Status</th>
                <th>Version</th>
                <th>Activity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMachines.map((m) => (
                <Fragment key={m.id}>
                  <tr className="machine-main-row" data-testid={`machine-row-${m.name}`}>
                    <td>
                      <strong>{m.name}</strong>
                    </td>
                    <td>
                      <span className="badge">{m.platform}</span>
                    </td>
                    <td>
                      <span className={`status ${m.online ? "on" : "off"}`}>
                        {m.online ? "Online" : "Offline"}
                      </span>
                    </td>
                    <td className="small">
                      <MachineVersionCell view={updates.views?.get(m.id)} />
                    </td>
                    <td className="muted small">
                      {m.online
                        ? "Connected"
                        : m.lastSeenAt
                          ? `Last seen ${new Date(m.lastSeenAt).toLocaleString()}`
                          : "Never connected"}
                    </td>
                    <td>
                      <button
                        className="link-btn danger"
                        data-testid={`revoke-${m.name}`}
                        onClick={() => setRevoking(m)}
                      >
                        Revoke…
                      </button>
                    </td>
                  </tr>
                  <tr className="machine-update-row" data-testid={`machine-updates-${m.name}`}>
                    <td colSpan={6}>
                      <MachineUpdateCell
                        machineName={m.name}
                        view={updates.views?.get(m.id)}
                        savingChannel={savingChannel.has(m.id)}
                        channelError={channelErrors.get(m.id) ?? null}
                        onChannelChange={(channel) => changeChannel(m.id, channel)}
                        onUpdateNow={() => setUpdating(m)}
                      />
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <p className="muted small">
        Viewing or ending browser sessions is not available yet. Revoking works even if the Machine is
        offline.
      </p>

      {updating && (
        <UpdateConfirmDialog
          machineId={updating.id}
          machineName={updating.name}
          prepare={updates.prepare}
          start={updates.start}
          onClose={() => setUpdating(null)}
        />
      )}

      {revoking && (
        <ConfirmDialog
          title={revokeConfirmation(revoking.name).title}
          message={
            <>
              <p>{revokeConfirmation(revoking.name).message}</p>
              <p className="muted small">{revokeConfirmation(revoking.name).consequence}</p>
            </>
          }
          confirmLabel={revokeConfirmation(revoking.name).confirmLabel}
          danger
          onClose={() => setRevoking(null)}
          onConfirm={async () => {
            try {
              await api.revokeMachine(revoking.id);
            } catch (err) {
              throw new Error(errorText(err));
            }
            setDone(`${revoking.name} was revoked.`);
            reload();
          }}
        />
      )}
    </>
  );
}
